import React, { useState, useContext, useEffect, useCallback } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import * as S from "./LoginStyle";
import { formatNumber } from "../../assets/utils";
import { usePulse } from "../../context/LoadContext";
import {
  FiEye,
  FiEyeOff,
  FiTrendingUp,
  FiArrowDown,
  FiInfo,
  FiTool,
} from "react-icons/fi";
import { MANUTENCAO_ATIVA, MANUTENCAO_MENSAGEM } from "../../config/manutencao";

const ResultValue = React.memo(({ prefix = "R$ ", value }) => {
  return (
    <S.ResultValue>
      {prefix}
      {formatNumber(value || 0)}
    </S.ResultValue>
  );
});

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoginView, setIsLoginView] = useState(true);
  const [tentouEntrar, setTentouEntrar] = useState(false);

  // Estados para simulação (Iniciando em 50 contratos: 21.250)
  const [valorInvestimento, setValorInvestimento] = useState("21250");
  const [currentPercent, setCurrentPercent] = useState(0);
  const [simulacaoData, setSimulacaoData] = useState({
    dailyProfit: 0,
    monthlyProfit: 0,
    yearlyProfit: 0,
    totalProfit: 0,
  });

  const navigate = useNavigate();
  const { login, error } = useContext(AuthContext);
  const { showPulse, hidePulse } = usePulse();

  // Cálculo estático com base nas faixas de contratos
  const calculateStaticSimulation = useCallback((valor) => {
    const PRECO_CONTRATO = 425;
    const valorNumerico = parseFloat(valor) || 0;
    const qtdContratos = Math.floor(valorNumerico / PRECO_CONTRATO);

    let taxa = 0;

    // Definição das regras de porcentagem solicitadas
    if (qtdContratos >= 200) {
      taxa = 4.1; // Acima de 200 contratos
    } else if (qtdContratos >= 100) {
      taxa = 3.1; // De 100 a 199 contratos
    } else if (qtdContratos >= 50) {
      taxa = 2.1; // De 50 a 99 contratos
    } else {
      taxa = 0; // Mínimo de 50 contratos
    }

    setCurrentPercent(taxa);

    const monthlyProfit = valorNumerico * (taxa / 100);
    const dailyProfit = monthlyProfit / 30;
    const yearlyProfit = monthlyProfit * 12;
    const totalProfit = valorNumerico + yearlyProfit;

    setSimulacaoData({
      dailyProfit,
      monthlyProfit,
      yearlyProfit,
      totalProfit,
    });
  }, []);

  useEffect(() => {
    calculateStaticSimulation(valorInvestimento);
  }, [valorInvestimento, calculateStaticSimulation]);

  const handleLogin = async (e) => {
    e.preventDefault();
    // Manutenção: não tenta logar, só destaca o aviso embaixo do formulário.
    if (MANUTENCAO_ATIVA) {
      setTentouEntrar(true);
      return;
    }
    if (!username || !password) return;
    showPulse();
    try {
      await login(username.trim(), password.trim());
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
    } finally {
      hidePulse();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setValorInvestimento(value);
    }
  };

  return (
    <S.PageContainer>
      <S.MainBox>
        <S.ToggleButton onClick={() => setIsLoginView(!isLoginView)}>
          {isLoginView ? <FiTrendingUp /> : <FiArrowDown />}
          <span>
            {isLoginView ? "Simular Valorização" : "Voltar para Login"}
          </span>
        </S.ToggleButton>

        <S.ContentWrapper
          className={isLoginView ? "login-active" : "simulacao-active"}
        >
          {/* VISTA DE LOGIN */}
          <S.LoginView>
            <S.Logo src="logo-golden.png" alt="Golden Brasil Logo" />
            <S.Title>Acesse sua Conta</S.Title>
            <S.LoginForm onSubmit={handleLogin}>
              <S.FormGroup>
                <S.InputLabel>Usuário, Email ou CPF/CNPJ</S.InputLabel>
                <S.Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </S.FormGroup>
              <S.FormGroup>
                <S.InputLabel>Senha</S.InputLabel>
                <S.InputWrapper>
                  <S.Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <S.ShowPassButton
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </S.ShowPassButton>
                </S.InputWrapper>
              </S.FormGroup>
              <S.Link href="/recover">Esqueceu a senha?</S.Link>
              <S.SubmitButton type="submit">Entrar</S.SubmitButton>
            </S.LoginForm>

            <S.FooterText>
              Não tem uma conta? <S.Link href="/cadastro">Cadastre-se</S.Link>
            </S.FooterText>
            {MANUTENCAO_ATIVA ? (
              <S.MaintenanceNotice $destaque={tentouEntrar} role="status">
                <FiTool />
                <span>{MANUTENCAO_MENSAGEM}</span>
              </S.MaintenanceNotice>
            ) : (
              error && <S.ErrorMessage>{error}</S.ErrorMessage>
            )}
          </S.LoginView>

          {/* VISTA DE SIMULAÇÃO */}
          <S.SimulacaoView>
            <S.Title>Simulador de Ganhos</S.Title>
            <S.SimulationForm>
              <S.InvestmentInputGroup>
                <S.InvestmentLabel>Seu Aporte Inicial</S.InvestmentLabel>
                <S.InvestmentInputContainer>
                  <span>R$</span>
                  <S.InvestmentInput
                    type="text"
                    value={valorInvestimento}
                    onChange={handleInputChange}
                  />
                </S.InvestmentInputContainer>

                <S.RateBadge active={currentPercent > 0}>
                  {currentPercent > 0
                    ? `Taxa aplicada: ${currentPercent}% ao mês`
                    : "Mínimo de 50 contratos para valorização"}
                </S.RateBadge>
              </S.InvestmentInputGroup>

              <S.SliderWrapper>
                <S.SliderInput
                  type="range"
                  min="21250"
                  max="1000000"
                  step="425"
                  value={valorInvestimento}
                  onChange={handleInputChange}
                />
              </S.SliderWrapper>

              <S.RulesInfo>
                <FiInfo />
                <div>
                  <span>
                    50-99 contr.: <strong>2.1% ao mês</strong>
                  </span>
                  <span>
                    100-199 contr.: <strong>3.1% ao mês</strong>
                  </span>
                  <span>
                    200+ contr.: <strong>4.1% ao mês</strong>
                  </span>
                </div>
              </S.RulesInfo>

              <S.ResultsGrid>
                <S.ResultBox>
                  <S.ResultLabel>Por Dia</S.ResultLabel>
                  <ResultValue value={simulacaoData.dailyProfit} />
                </S.ResultBox>
                <S.ResultBox>
                  <S.ResultLabel>Por Mês</S.ResultLabel>
                  <ResultValue value={simulacaoData.monthlyProfit} />
                </S.ResultBox>
                <S.ResultBox highlight>
                  <S.ResultLabel>Total (Aporte + 12 meses)</S.ResultLabel>
                  <ResultValue value={simulacaoData.totalProfit} />
                </S.ResultBox>
              </S.ResultsGrid>
            </S.SimulationForm>

            <S.FooterText>
              Interessado nessa rentabilidade?{" "}
              <S.Link href="/cadastro">Abra sua conta</S.Link>
            </S.FooterText>
          </S.SimulacaoView>
        </S.ContentWrapper>
      </S.MainBox>
    </S.PageContainer>
  );
};

export default LoginPage;
